<template>
    <Teleport :to="props.appendTo">
        <div class="modal fade" :class="props.class" role="dialog" aria-hidden="true" ref="modalElement">
            <div :class="['modal-dialog modal-dialog-centered', size]" role="document">
                <div class="modal-content">
                    <div v-if="hasHeader" class="modal-header">
                        <h5 class="modal-title">{{ title }}</h5>
                        <button v-if="closable" type="button" class="close" @click="close" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <slot name="body"></slot>
                    </div>
                    <div v-if="hasFooter" class="modal-footer">
                        <slot name="footer"></slot>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup lang="ts">
    import { computed, onMounted, onUnmounted, ref } from "vue";

    const props = withDefaults(
        defineProps<{
            appendTo?: string;
            title?: string;
            class?: string;
            size?: string;
            closable?: boolean;
            hasHeader?: boolean;
            hasFooter?: boolean;
            disposeAfterHide?: boolean;
        }>(),
        {
            appendTo: "body",
            title: "",
            size: "",
            closable: true,
            hasHeader: true,
            hasFooter: true,
            disposeAfterClose: false,
        }
    );

    const emit = defineEmits(["close", "show", "hide"]);

    const modalElement = ref<HTMLElement | null>(null);
    const $modalElement = computed(() => $(modalElement.value as any));
    const isVisible = ref<boolean>(false);

    const show = () => {
        $modalElement.value["modal"]({
            keyboard: props.closable,
        });
        emit("show");
        isVisible.value = true;
    };

    const close = () => {
        $modalElement.value["modal"]("hide");
        emit("close");
        isVisible.value = false;
    };

    function onShown() {
        isVisible.value = true;
        emit("show");
    }

    function onHidden() {
        isVisible.value = false;

        if (props.disposeAfterHide) {
            $modalElement.value?.remove();
        }

        emit("hide");
    }

    onMounted(() => {
        $modalElement.value.on("shown.bs.modal", () => onShown());
        $modalElement.value.on("hidden.bs.modal", () => onHidden());
    });

    onUnmounted(() => {
        $modalElement.value.off("shown.bs.modal");
        $modalElement.value.off("hidden.bs.modal");
    });

    // define exposed
    defineExpose({
        show,
        close,
        isVisible,
    });
</script>
